import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { useSearchParams } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import EChart from "../components/EChart";
import IndustryPicker from "../components/IndustryPicker";
import {
  changeAnalysisIndustry,
  correctAnalysisLines,
  createAnalysisJob,
  getAnalysisJob,
  listAnalysisJobs,
} from "../services/analysisApi";
import "./style/finanalystReportPage.css";
import "./style/finanalystV2.css";

const PRICE = 5;
const POLL_MS = 3000;
const RUNNING = ["queued", "processing"];
const MAX_TOTAL = 45 * 1024 * 1024;

// ---------- formátování ----------
const nf0 = new Intl.NumberFormat("cs-CZ", { maximumFractionDigits: 0 });
const nf1 = new Intl.NumberFormat("cs-CZ", { maximumFractionDigits: 1, minimumFractionDigits: 1 });
const nf2 = new Intl.NumberFormat("cs-CZ", { maximumFractionDigits: 2, minimumFractionDigits: 2 });
const isNum = (v) => v !== null && v !== undefined && !Number.isNaN(v);
const fmtMil = (tis) => (isNum(tis) ? `${nf1.format(tis / 1000)} mil. Kč` : "—");
const fmtTis = (tis) => (isNum(tis) ? nf0.format(tis) : "—");
const fmtPct = (v) => (isNum(v) ? `${nf1.format(v * 100)} %` : "—");
const fmtMetric = (m, v) => {
  if (!isNum(v)) return "—";
  if (m.percent) return fmtPct(v);
  if (m.days || m.metric.startsWith("Obrat")) return `${nf0.format(v)} dní`;
  return nf2.format(v);
};

const STATUS_LABEL = {
  queued: "Ve frontě",
  processing: "Zpracovává se",
  ready: "Hotovo",
  needs_review: "Ke kontrole",
  error: "Chyba",
};
const SECTION_LABEL = { aktiva: "Aktiva", pasiva: "Pasiva", vzz: "Výkaz zisku a ztráty" };
const METRIC_GROUPS = [
  ["Ziskovost", ["EBITDA marže", "Provozní marže", "ROA", "ROE", "ROS"]],
  ["Zadluženost a likvidita", ["Zadluženost celková", "Zadluženost dlouhodobá", "Likvidita běžná", "Likvidita pohotová", "Likvidita okamžitá"]],
  ["Efektivita", ["Obrat aktiv", "Obrat zásob"]],
  ["Pracovní kapitál", ["Doba obratu pohledávek", "Doba obratu závazků", "Cyklus peněz"]],
  ["Struktura nákladů a přidaná hodnota", ["Podíl osobních nákladů", "Podíl výkonové spotřeby", "Marže přidané hodnoty", "Osobní náklady / přidaná hodnota"]],
  ["Růst", ["Meziroční růst tržeb"]],
];
const TREND_TABS = ["EBITDA marže", "ROA", "ROE", "Zadluženost celková"];
// bez těchto řádků analýza nedává smysl; ostatní ve formuláři chybí běžně (nulové řádky se netisknou)
const CORE_LINES = new Set([
  "aktiva_celkem", "stala_aktiva", "obezna_aktiva", "pasiva_celkem", "vlastni_kapital", "cizi_zdroje",
  "trzby_vyrobky", "vh_pred_zdanenim",
]);
const REVIEW_LINES = [
  "aktiva_celkem", "stala_aktiva", "obezna_aktiva", "zasoby", "pohledavky", "penize",
  "pasiva_celkem", "vlastni_kapital", "vh_bezneho_obdobi", "cizi_zdroje", "zav_dlouhodobe", "zav_kratkodobe",
  "trzby_vyrobky", "trzby_zbozi", "vykonova_spotreba", "osobni_naklady", "upravy_hodnot_provozni",
  "upravy_hodnot_dm", "provozni_vh", "nakladove_uroky", "vh_pred_zdanenim", "vh_za_obdobi",
];

const C = { accent: "#13a870", ink: "#0f172a", muted: "#94a3b8", line: "#e2e8f0", label: "#64748b" };

// ---------- grafy ----------
function revenueOption(rows) {
  const data = [...rows].filter((r) => r.Rok && (isNum(r["Tržby"]) || isNum(r.EBITDA))).sort((a, b) => a.Rok - b.Rok);
  const bar = (key, color) =>
    data.map((r) => ({
      value: isNum(r[key]) ? +(r[key] / 1000).toFixed(1) : null,
      itemStyle: { color },
    }));
  return {
    // legenda vpravo nahoře, název osy vlevo -> nepřekrývají se; containLabel drží popisky osy uvnitř
    grid: { left: 8, right: 16, top: 48, bottom: 8, containLabel: true },
    legend: { top: 0, right: 0, itemWidth: 12, itemHeight: 12, itemGap: 16, textStyle: { color: "#475569" } },
    tooltip: { trigger: "axis", valueFormatter: (v) => (isNum(v) ? `${nf1.format(v)} mil. Kč` : "—") },
    xAxis: { type: "category", data: data.map((r) => String(r.Rok)), axisTick: { show: false }, axisLine: { lineStyle: { color: C.line } }, axisLabel: { color: C.label } },
    yAxis: { type: "value", name: "mil. Kč", nameGap: 14, nameTextStyle: { color: C.label, align: "left" }, axisLabel: { color: C.label }, splitLine: { lineStyle: { color: C.line } } },
    series: [
      { name: "Tržby", type: "bar", data: bar("Tržby", C.ink), barMaxWidth: 26 },
      { name: "EBITDA", type: "bar", data: bar("EBITDA", C.accent), barMaxWidth: 26 },
    ],
  };
}

function trendOption(rows) {
  const data = (rows || []).filter((r) => isNum(r.firm) || isNum(r.median));
  const v = (x) => (isNum(x) ? +(x * 100).toFixed(1) : null);
  return {
    grid: { left: 8, right: 16, top: 48, bottom: 8, containLabel: true },
    legend: { top: 0, right: 0, itemWidth: 12, itemHeight: 12, itemGap: 16, textStyle: { color: "#475569" } },
    tooltip: { trigger: "axis", valueFormatter: (x) => (isNum(x) ? `${nf1.format(x)} %` : "—") },
    xAxis: { type: "category", data: data.map((r) => String(r.year)), axisTick: { show: false }, axisLine: { lineStyle: { color: C.line } }, axisLabel: { color: C.label } },
    yAxis: { type: "value", axisLabel: { formatter: "{value} %", color: C.label }, splitLine: { lineStyle: { color: C.line } } },
    series: [
      { name: "Firma", type: "bar", data: data.map((r) => v(r.firm)), barMaxWidth: 26, itemStyle: { color: C.accent } },
      { name: "Medián oboru", type: "line", data: data.map((r) => v(r.median)), symbol: "circle", symbolSize: 6,
        lineStyle: { color: C.ink, width: 2 }, itemStyle: { color: C.ink } },
      { name: "Hranice nejlepší čtvrtiny", type: "line", data: data.map((r) => v(r.q3)), symbol: "none",
        lineStyle: { color: C.muted, type: "dashed", width: 1.5 }, itemStyle: { color: C.muted } },
    ],
  };
}

// ---------- drobné komponenty ----------
function PercentileBar({ value, oriented }) {
  if (!isNum(value)) return <span className="fa2-muted">—</span>;
  const tone = !oriented ? "neutral" : value >= 60 ? "good" : value >= 40 ? "mid" : "bad";
  return (
    <div className="fa2-pbar" title={`Lepší než ${nf0.format(value)} % firem`}>
      <div className="fa2-pbar__track">
        <div className="fa2-pbar__median" />
        <div className={`fa2-pbar__marker fa2-pbar__marker--${tone}`} style={{ left: `${Math.min(100, Math.max(0, value))}%` }} />
      </div>
      <span className="fa2-pbar__label">{nf0.format(value)}</span>
    </div>
  );
}

function Kpi({ label, value, sub }) {
  return (
    <div className="fa2-kpi">
      <div className="fa2-kpi__label">{label}</div>
      <div className="fa2-kpi__value">{value}</div>
      {sub && <div className="fa2-kpi__sub">{sub}</div>}
    </div>
  );
}

function Card({ title, actions, children, className = "" }) {
  return (
    <section className={`fa2-card ${className}`}>
      {(title || actions) && (
        <div className="fa2-card__head">
          {title && <h2>{title}</h2>}
          {actions && <div className="fa2-actions fa2-no-print">{actions}</div>}
        </div>
      )}
      {children}
    </section>
  );
}

// ---------- bloky výsledku ----------
function ValuationCard({ valuation }) {
  if (!valuation?.available) {
    return (
      <Card title="Odhad hodnoty podniku">
        <p className="fa2-muted">Ocenění nebylo možné spočítat (chybí data).</p>
      </Card>
    );
  }
  const r = valuation.range || {};
  const equity = !valuation.method?.startsWith("EBITDA");
  return (
    <Card title="Odhad hodnoty podniku" className="fa2-valuation">
      {valuation.valuable ? (
        <>
          <div className="fa2-valuation__center">{fmtMil(r.center)}</div>
          <div className="fa2-valuation__range">rozpětí {fmtMil(r.low)} – {fmtMil(r.high)}</div>
        </>
      ) : (
        <div className="fa2-valuation__note">{valuation.note}</div>
      )}
      <dl className="fa2-dl">
        <dt>Metoda</dt>
        <dd>{equity ? "Majetková (vlastní kapitál) + ziskovost" : "Násobek EBITDA + korekce aktiv"}</dd>
        <dt>Průměrná EBITDA (3 roky)</dt>
        <dd>{fmtMil(valuation.average_ebitda_last_3y)}</dd>
        {!equity && valuation.multiplier?.raw_center !== undefined && (
          <>
            <dt>Násobek EBITDA</dt>
            <dd>{nf2.format(valuation.multiplier.final_low)}× – {nf2.format(valuation.multiplier.final_high)}×</dd>
          </>
        )}
      </dl>
      {(valuation.newer_years_in_registry || valuation.newer_years_in_merk)?.length > 0 && (
        <p className="fa2-note">
          V registru jsou i novější data ({(valuation.newer_years_in_registry || valuation.newer_years_in_merk).join(", ")}). Analýza vychází z nahraného výkazu.
        </p>
      )}
    </Card>
  );
}

function StrengthsCard({ metrics }) {
  const oriented = (metrics || []).filter((m) => m.oriented && isNum(m.industry_percentile));
  const best = [...oriented].sort((a, b) => b.industry_percentile - a.industry_percentile).filter((m) => m.industry_percentile >= 60).slice(0, 3);
  const worst = [...oriented].sort((a, b) => a.industry_percentile - b.industry_percentile).filter((m) => m.industry_percentile <= 40).slice(0, 3);
  const Row = ({ m }) => (
    <li>
      <span>{m.metric}</span>
      <span className="fa2-num fa2-strong">{fmtMetric(m, m.value)}</span>
      <span className="fa2-muted fa2-small">lepší než {nf0.format(m.industry_percentile)} % oboru</span>
    </li>
  );
  return (
    <Card title="Silné a slabé stránky">
      <div className="fa2-sw">
        <div>
          <h3 className="fa2-sw__good">Silné stránky</h3>
          {best.length ? <ul className="fa2-sw__list">{best.map((m) => <Row key={m.metric} m={m} />)}</ul> : <p className="fa2-muted fa2-small">Žádný ukazatel výrazně nad oborem.</p>}
        </div>
        <div>
          <h3 className="fa2-sw__bad">Slabé stránky</h3>
          {worst.length ? <ul className="fa2-sw__list">{worst.map((m) => <Row key={m.metric} m={m} />)}</ul> : <p className="fa2-muted fa2-small">Žádný ukazatel výrazně pod oborem.</p>}
        </div>
      </div>
    </Card>
  );
}

function ScenariosCard({ scenarios, center }) {
  if (!scenarios?.length) return null;
  const maxAbs = Math.max(...scenarios.map((s) => Math.abs(s.delta_pct)), 0.01);
  return (
    <Card title="Co zvýší hodnotu">
      <p className="fa2-muted fa2-small">Stejný oceňovací model, jen se změněnými vstupy. Výchozí střední hodnota {fmtMil(center)}.</p>
      <ul className="fa2-scen">
        {scenarios.map((s) => (
          <li key={s.key}>
            <div className="fa2-scen__text">
              <strong>{s.title}</strong>
              <span className="fa2-muted fa2-small">{s.detail}</span>
            </div>
            <div className="fa2-scen__bar">
              <div className={`fa2-scen__fill${s.delta < 0 ? " is-neg" : ""}`} style={{ width: `${(Math.abs(s.delta_pct) / maxAbs) * 100}%` }} />
            </div>
            <div className="fa2-scen__val fa2-num">
              <span className="fa2-strong">{s.delta >= 0 ? "+" : "−"}{fmtMil(Math.abs(s.delta))}</span>
              <span className="fa2-muted fa2-small">{s.delta >= 0 ? "+" : "−"}{nf0.format(Math.abs(s.delta_pct * 100))} %</span>
            </div>
          </li>
        ))}
      </ul>
    </Card>
  );
}

function ChartsCard({ valuation, trends }) {
  const [tab, setTab] = useState(TREND_TABS[0]);
  const rev = useMemo(() => revenueOption(valuation?.history || []), [valuation]);
  const trend = useMemo(() => trendOption(trends?.[tab]), [trends, tab]);
  const hasTrend = (trends?.[tab] || []).length > 0;
  return (
    <div className="fa2-grid2">
      <Card title="Tržby a EBITDA">
        <EChart option={rev} height={260} />
      </Card>
      <Card title="Vývoj proti oboru">
        <div className="fa2-tabs fa2-no-print">
          {TREND_TABS.map((t) => (
            <button key={t} type="button" className={`fa2-tab${t === tab ? " is-active" : ""}`} onClick={() => setTab(t)}>
              {t.replace("Zadluženost celková", "Zadluženost")}
            </button>
          ))}
        </div>
        {hasTrend ? <EChart option={trend} height={236} /> : <p className="fa2-muted">Pro tento ukazatel nejsou data.</p>}
      </Card>
    </div>
  );
}

function BenchmarkCard({ benchmark }) {
  if (!benchmark?.available) {
    return (
      <Card title="Srovnání s oborem">
        <p className="fa2-muted">Obor firmy se nepodařilo určit. Vyberte obor ve formuláři a spusťte analýzu znovu.</p>
      </Card>
    );
  }
  const byName = Object.fromEntries((benchmark.metrics || []).map((m) => [m.metric, m]));
  const ig = benchmark.industry_group;
  const sg = benchmark.size_group;
  return (
    <Card title="Srovnání s oborem">
      <div className="fa2-chips">
        {ig && <span className="fa2-chip">Obor: {ig.name} · {ig.n} firem</span>}
        {sg && <span className="fa2-chip">Podobná velikost: {sg.name} · {sg.n} firem</span>}
      </div>
      {benchmark.notes?.map((n) => <p key={n} className="fa2-note">{n}</p>)}
      <div className="fa2-table-wrap">
        <table className="fa2-table fa2-bench">
          <colgroup>
            <col className="c-name" /><col className="c-num" /><col className="c-num" /><col className="c-bar" /><col className="c-num" /><col className="c-bar" />
          </colgroup>
          <thead>
            <tr>
              <th>Ukazatel</th>
              <th className="num">Firma</th>
              <th className="num">Medián oboru</th>
              <th>Pozice v oboru</th>
              <th className="num">Medián podobných</th>
              <th>Pozice mezi podobnými</th>
            </tr>
          </thead>
          {METRIC_GROUPS.map(([group, names]) => {
            const rows = names.map((n) => byName[n]).filter((m) => m && (isNum(m.value) || isNum(m.industry?.median)));
            if (!rows.length) return null;
            return (
              <tbody key={group}>
                <tr className="fa2-group"><td colSpan={6}>{group}</td></tr>
                {rows.map((m) => (
                  <tr key={m.metric}>
                    <td>
                      {m.metric}
                      {m.lower_is_better && <span className="fa2-hint" title="Nižší hodnota je lepší"> ↓</span>}
                    </td>
                    <td className="num fa2-strong">{fmtMetric(m, m.value)}</td>
                    <td className="num">{fmtMetric(m, m.industry?.median)}</td>
                    <td><PercentileBar value={m.industry_percentile} oriented={m.oriented} /></td>
                    <td className="num">{fmtMetric(m, m.size?.median)}</td>
                    <td><PercentileBar value={m.size_percentile} oriented={m.oriented} /></td>
                  </tr>
                ))}
              </tbody>
            );
          })}
        </table>
      </div>
      <p className="fa2-muted fa2-small">
        Pozice = o kolik procent firem je firma lepší (50 = medián). U zadluženosti a doby obratu je lepší nižší hodnota;
        u podílu nákladů jde jen o pozici, ne hodnocení.
      </p>
    </Card>
  );
}

function PeersCard({ peers }) {
  if (!peers?.length) return null;
  return (
    <Card title="Podobné firmy a konkurence">
      <p className="fa2-muted fa2-small">Firmy s podobnou činností, zaměřením, regionem a velikostí (poslední dostupný rok v registru).</p>
      <div className="fa2-table-wrap">
        <table className="fa2-table fa2-peers">
          <colgroup><col className="c-firm" /><col className="c-region" /><col className="c-num" /><col className="c-num" /><col className="c-num" /></colgroup>
          <thead>
            <tr>
              <th>Firma</th>
              <th>Kraj</th>
              <th className="num">Tržby</th>
              <th className="num">EBITDA marže</th>
              <th className="num">Zadluženost</th>
            </tr>
          </thead>
          <tbody>
            {peers.map((p) => (
              <tr key={p.ico}>
                <td>
                  <div className="fa2-strong">{p.name}</div>
                  <div className="fa2-muted fa2-small">IČO {p.ico}{p.obor ? ` · ${p.obor}` : ""}</div>
                  {p.reasons?.length > 0 && (
                    <div className="fa2-tags">{p.reasons.map((r) => <span key={r} className="fa2-tag">{r}</span>)}</div>
                  )}
                </td>
                <td>{p.region || "—"}</td>
                <td className="num">{fmtMil(p.trzby)}</td>
                <td className="num">{fmtPct(p.ebitda_marze)}</td>
                <td className="num">{fmtPct(p.zadluzenost)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </Card>
  );
}

function StatementCard({ result, onSave, saving }) {
  const lines = result.statement.lines;
  const v = result.validation;
  const problems = (v.failed?.length || 0) + (v.missing_core?.length || 0) + (v.missing_sections?.length || 0);
  const [open, setOpen] = useState(problems > 0);
  const [edit, setEdit] = useState(false);
  const [draft, setDraft] = useState({});

  const change = (key, period, value) => setDraft((d) => ({ ...d, [key]: { ...(d[key] || {}), [period]: value } }));
  const save = async () => {
    const payload = {};
    Object.entries(draft).forEach(([key, val]) => {
      payload[key] = {};
      ["cur", "prev"].forEach((p) => {
        if (p in val) payload[key][p] = val[p] === "" ? null : Number(String(val[p]).replace(/\s/g, "").replace(",", "."));
      });
    });
    await onSave(payload);
    setDraft({});
    setEdit(false);
  };
  const bySection = REVIEW_LINES.reduce((acc, key) => {
    const ln = lines[key];
    if (ln) (acc[ln.section] = acc[ln.section] || []).push([key, ln]);
    return acc;
  }, {});

  return (
    <Card
      title="Čísla z výkazů"
      className="fa2-statement"
      actions={
        <>
          {open && !edit && <button type="button" className="fa2-btn fa2-btn--ghost" onClick={() => setEdit(true)}>Upravit čísla</button>}
          {edit && (
            <>
              <button type="button" className="fa2-btn fa2-btn--primary" onClick={save} disabled={saving}>{saving ? "Přepočítávám…" : "Uložit a přepočítat"}</button>
              <button type="button" className="fa2-btn fa2-btn--ghost" onClick={() => { setEdit(false); setDraft({}); }}>Zrušit</button>
            </>
          )}
          <button type="button" className="fa2-btn fa2-btn--ghost" onClick={() => setOpen((o) => !o)}>{open ? "Skrýt" : "Zobrazit"}</button>
        </>
      }
    >
      <p className={`fa2-check ${problems ? "is-warn" : "is-ok"}`}>
        {problems
          ? "Některá čísla neprošla kontrolou – zkontrolujte je prosím podle výkazu."
          : `Všechny účetní kontroly sedí (${v.passed}/${v.checked}). Hodnoty v tis. Kč.`}
      </p>
      {open && (
        <>
          {problems > 0 && (
            <ul className="fa2-warning">
              {v.missing_sections?.map((s) => <li key={s}>Chybí výkaz: {s}. Nahrajte ho prosím.</li>)}
              {v.missing_core?.map((s) => <li key={s}>Nenalezen řádek: {s}</li>)}
              {v.failed?.map((f) => <li key={f}>Nesedí vazba: {f.replace("[cur]", "(běžné období)").replace("[prev]", "(minulé období)")}</li>)}
            </ul>
          )}
          <div className="fa2-grid3">
            {Object.entries(bySection).map(([section, rows]) => (
              <div key={section} className="fa2-section">
                <h3>{SECTION_LABEL[section]}</h3>
                <table className="fa2-table fa2-table--compact fa2-lines">
                  <colgroup><col /><col className="c-num" /><col className="c-num" /></colgroup>
                  <thead>
                    <tr><th>Řádek</th><th className="num">Běžné</th><th className="num">Minulé</th></tr>
                  </thead>
                  <tbody>
                    {rows.map(([key, ln]) => {
                      const missingCore = ln.state === "missing" && CORE_LINES.has(key);
                      return (
                        <tr key={key} className={missingCore ? "fa2-row--missing" : ""}>
                          <td>
                            {ln.code && <span className="fa2-code">{ln.code}</span>}
                            {ln.label}
                            {ln.state === "missing" && (missingCore ? <span className="fa2-badge">nenalezeno</span> : <span className="fa2-muted fa2-small"> · neuvedeno</span>)}
                          </td>
                          {["cur", "prev"].map((p) => (
                            <td key={p} className="num">
                              {edit ? (
                                <input className="fa2-num-input" inputMode="decimal" value={draft[key]?.[p] ?? (ln[p] ?? "")} onChange={(e) => change(key, p, e.target.value)} />
                              ) : ln.state === "missing" ? "—" : fmtTis(ln[p] ?? 0)}
                            </td>
                          ))}
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            ))}
          </div>
        </>
      )}
    </Card>
  );
}

// ---------- změna oboru u hotové analýzy (jako v rychlé analýze) ----------
function IndustryEditor({ company, onChange, busy }) {
  const [open, setOpen] = useState(false);
  const [code, setCode] = useState("");
  const apply = async (value) => {
    await onChange(value);
    setOpen(false);
    setCode("");
  };
  if (!open) {
    return (
      <div className="fa2-industry">
        <span className="fa2-muted">Obor pro srovnání:</span>
        <strong>{company.obor || "neurčen"}</strong>
        {company.obor_zmenen_uzivatelem && <span className="fa2-badge fa2-badge--info">zvolen ručně</span>}
        <button type="button" className="fa2-btn fa2-btn--ghost fa2-btn--sm fa2-no-print" onClick={() => setOpen(true)}>
          Změnit obor
        </button>
      </div>
    );
  }
  return (
    <div className="fa2-industry fa2-industry--edit fa2-no-print">
      <div className="fa2-industry__picker">
        <IndustryPicker value={code} onChange={setCode} placeholder="Začněte psát obor, např. výroba strojů" />
      </div>
      <button type="button" className="fa2-btn fa2-btn--primary fa2-btn--sm" disabled={!code || busy} onClick={() => apply(code)}>
        {busy ? "Přepočítávám…" : "Použít"}
      </button>
      {company.obor_zmenen_uzivatelem && (
        <button type="button" className="fa2-btn fa2-btn--ghost fa2-btn--sm" disabled={busy} onClick={() => apply(null)}>
          Obor z registru
        </button>
      )}
      <button type="button" className="fa2-btn fa2-btn--ghost fa2-btn--sm" disabled={busy} onClick={() => setOpen(false)}>
        Zrušit
      </button>
      <p className="fa2-muted fa2-small">
        Přepočítá se srovnání s oborem, ocenění, vývoj proti oboru a shrnutí. Čísla z výkazů zůstanou. Zdarma.
      </p>
    </div>
  );
}

// ---------- stránka ----------
function FinanalystV2Page() {
  const { reload } = useAuth();
  const [files, setFiles] = useState([]);
  const [ico, setIco] = useState("");
  const [nace, setNace] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [job, setJob] = useState(null);
  const [history, setHistory] = useState([]);
  const [saving, setSaving] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const pollRef = useRef(null);
  const fileInput = useRef(null);
  const resultRef = useRef(null);

  const loadHistory = useCallback(async () => {
    try {
      const data = await listAnalysisJobs();
      setHistory(data.jobs || []);
    } catch (e) {
      /* historie není kritická */
    }
  }, []);

  useEffect(() => {
    loadHistory();
    return () => clearTimeout(pollRef.current);
  }, [loadHistory]);

  // odkaz z chatu: /app/financni-analyza?job=<id>
  const [searchParams] = useSearchParams();
  const jobParam = searchParams.get("job");

  const poll = useCallback(
    async (id, scroll = false) => {
      clearTimeout(pollRef.current);
      try {
        const data = await getAnalysisJob(id);
        setJob(data);
        if (scroll) setTimeout(() => resultRef.current?.scrollIntoView({ behavior: "smooth", block: "start" }), 50);
        if (RUNNING.includes(data.status)) {
          pollRef.current = setTimeout(() => poll(id), POLL_MS);
        } else {
          loadHistory();
          if (data.credits_refunded && reload) reload();
        }
      } catch (e) {
        setError(e.message);
      }
    },
    [loadHistory, reload]
  );

  useEffect(() => {
    if (jobParam && /^[0-9a-f]{32}$/.test(jobParam)) poll(jobParam, true);
  }, [jobParam, poll]);

  const onFiles = (list) => {
    const pdfs = Array.from(list || []).filter((f) => f.type === "application/pdf" || f.name.toLowerCase().endsWith(".pdf"));
    setFiles((prev) => [...prev, ...pdfs].slice(0, 6));
  };

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (!files.length) return setError("Vyberte alespoň jeden PDF soubor.");
    if (files.reduce((s, f) => s + f.size, 0) > MAX_TOTAL) {
      return setError("Soubory mají dohromady víc než 45 MB. Nahrajte prosím jen výkazy (rozvahu a výkaz zisku a ztráty).");
    }
    setSubmitting(true);
    try {
      const created = await createAnalysisJob({ files, ico: ico.trim(), nace });
      if (reload) reload();
      setFiles([]);
      poll(created.id, true);
    } catch (err) {
      setError(err.message);
    } finally {
      setSubmitting(false);
    }
    return undefined;
  };

  const saveCorrections = async (lines) => {
    setSaving(true);
    try {
      setJob(await correctAnalysisLines(job.id, lines));
    } catch (e) {
      setError(e.message);
    } finally {
      setSaving(false);
    }
  };

  const saveIndustry = async (code) => {
    setSaving(true);
    setError("");
    try {
      setJob(await changeAnalysisIndustry(job.id, code));
    } catch (e) {
      setError(e.message);
    } finally {
      setSaving(false);
    }
  };

  const result = job?.result;
  const running = job && RUNNING.includes(job.status);
  const company = result?.company || {};
  const cur = result?.figures?.cur;
  const year = cur?.year;
  const val = result?.valuation;

  return (
    <div className="finanalyst-page fa2">
      <header className="fa2-hero fa2-no-print">
        <h1>Finanční analýza z výkazů</h1>
        <p>
          Nahrajte rozvahu a výkaz zisku a ztráty (PDF, i sken). Čísla přečteme a ověříme účetními vazbami, porovnáme firmu
          s oborem a s firmami podobné velikosti a odhadneme hodnotu podniku.
        </p>
      </header>

      <form className="fa2-card fa2-upload fa2-no-print" onSubmit={submit}>
        <div
          className={`fa2-drop${dragOver ? " is-over" : ""}`}
          onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
          onDragLeave={() => setDragOver(false)}
          onDrop={(e) => { e.preventDefault(); setDragOver(false); onFiles(e.dataTransfer.files); }}
          onClick={() => fileInput.current?.click()}
          role="button"
          tabIndex={0}
          onKeyDown={(e) => (e.key === "Enter" || e.key === " ") && fileInput.current?.click()}
        >
          <input ref={fileInput} type="file" accept="application/pdf" multiple hidden onChange={(e) => { onFiles(e.target.files); e.target.value = ""; }} />
          <div className="fa2-drop__icon" aria-hidden>⬆</div>
          <div><strong>Přetáhněte PDF sem</strong> nebo klikněte a vyberte soubory</div>
          <div className="fa2-muted fa2-small">Rozvaha, výkaz zisku a ztráty, celá účetní závěrka nebo výroční zpráva · max. 6 souborů</div>
        </div>
        {files.length > 0 && (
          <ul className="fa2-files">
            {files.map((f, i) => (
              <li key={`${f.name}-${i}`}>
                <span className="fa2-file__name">{f.name}</span>
                <span className="fa2-muted fa2-small">{nf1.format(f.size / 1024 / 1024)} MB</span>
                <button type="button" className="fa2-x" aria-label="Odebrat" onClick={() => setFiles((prev) => prev.filter((_, j) => j !== i))}>×</button>
              </li>
            ))}
          </ul>
        )}
        <div className="fa2-params">
          <label className="fa2-field">
            <span>IČO <em>nepovinné – přečteme z výkazu</em></span>
            <input className="finanalyst-input" value={ico} onChange={(e) => setIco(e.target.value)} inputMode="numeric" placeholder="např. 15057798" />
          </label>
          <div className="fa2-field">
            <span>Obor <em>nepovinné – zjistíme z registru</em></span>
            <IndustryPicker value={nace} onChange={setNace} />
          </div>
        </div>
        <div className="fa2-submit">
          <button className="fa2-btn fa2-btn--primary" type="submit" disabled={submitting || running}>
            {submitting ? "Nahrávám…" : "Spustit analýzu"}
          </button>
          <span className="fa2-muted fa2-small">Cena {PRICE} kreditů. Při chybě se kredity vrací.</span>
        </div>
        {error && <p className="finanalyst-error">{error}</p>}
      </form>

      <div ref={resultRef} />
      {job && (running || job.status === "error" || job.message) && (
        <div className={`fa2-status fa2-status--${job.status} fa2-no-print`}>
          {running && <div className="loader-spinner" />}
          <div>
            <strong>{STATUS_LABEL[job.status] || job.status}</strong>
            {running && <span> · {job.step || "Zpracovávám…"}</span>}
            {running && <div className="fa2-muted fa2-small">U skenů to může trvat několik minut. Stránku můžete nechat otevřenou.</div>}
            {job.message && <div>{job.message}</div>}
          </div>
        </div>
      )}

      {result && (
        <div className="fa2-result">
          <section className="fa2-company">
            <div>
              <h2>{company.name || "Firma"}{year ? <span className="fa2-muted"> · {year}</span> : null}</h2>
              <div className="fa2-muted">
                {company.ico ? `IČO ${company.ico}` : "IČO nenalezeno"}
                {company.velikost ? ` · ${company.velikost}` : ""}
              </div>
              <IndustryEditor company={company} onChange={saveIndustry} busy={saving} />
            </div>
            <div className="fa2-actions fa2-no-print">
              <button type="button" className="fa2-btn fa2-btn--ghost" onClick={() => window.print()}>Stáhnout PDF</button>
            </div>
          </section>

          <div className="fa2-kpis">
            <Kpi label="Tržby" value={fmtMil(cur?.values?.["Tržby"])} sub={year ? `rok ${year}` : null} />
            <Kpi label="EBITDA" value={fmtMil(cur?.values?.EBITDA)} sub={isNum(cur?.ratios?.["EBITDA marže"]) ? `marže ${fmtPct(cur.ratios["EBITDA marže"])}` : null} />
            <Kpi label="Vlastní kapitál" value={fmtMil(cur?.values?.["Vlastní kapitál"])} sub={isNum(cur?.ratios?.["Zadluženost celková"]) ? `zadluženost ${fmtPct(cur.ratios["Zadluženost celková"])}` : null} />
            <Kpi label="Zisk po zdanění" value={fmtMil(cur?.values?.["VH za období"])} sub={isNum(cur?.ratios?.ROE) ? `ROE ${fmtPct(cur.ratios.ROE)}` : null} />
          </div>

          {cur?.notes?.map((n) => <p key={n} className="fa2-note">{n}</p>)}

          <div className="fa2-grid2">
            <ValuationCard valuation={val} />
            <StrengthsCard metrics={result.benchmark?.metrics} />
          </div>

          {result.commentary && (
            <Card title="Shrnutí">
              {result.commentary_stale && <p className="fa2-note">Shrnutí vzniklo před úpravou čísel.</p>}
              <div className="fa2-md"><ReactMarkdown remarkPlugins={[remarkGfm]}>{result.commentary}</ReactMarkdown></div>
            </Card>
          )}

          <ScenariosCard scenarios={result.scenarios} center={val?.range?.center} />
          <ChartsCard valuation={val} trends={result.trends} />
          <BenchmarkCard benchmark={result.benchmark} />
          <PeersCard peers={result.peers} />
          <StatementCard result={result} onSave={saveCorrections} saving={saving} />
        </div>
      )}

      <Card title="Historie analýz" className="fa2-history fa2-no-print">
        {history.length === 0 ? (
          <p className="fa2-muted">Zatím žádné analýzy.</p>
        ) : (
          <table className="fa2-table fa2-table--compact">
            <thead>
              <tr><th>Datum</th><th>Firma</th><th className="num">Rok</th><th>Stav</th><th /></tr>
            </thead>
            <tbody>
              {history.map((h) => (
                <tr key={h.id} className={job?.id === h.id ? "is-current" : ""}>
                  <td className="fa2-nowrap">{h.created_at ? new Date(h.created_at).toLocaleString("cs-CZ") : ""}</td>
                  <td>{h.company_name || h.ico || h.files?.join(", ")}</td>
                  <td className="num">{h.report_year || ""}</td>
                  <td><span className={`fa2-pill fa2-pill--${h.status}`}>{STATUS_LABEL[h.status] || h.status}</span></td>
                  <td className="num"><button type="button" className="fa2-btn fa2-btn--ghost fa2-btn--sm" onClick={() => poll(h.id, true)}>Otevřít</button></td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Card>
    </div>
  );
}

export default FinanalystV2Page;
